/**
 * Which routes mount the Clerk auth shell.
 *
 * `ClerkProvider` and the Clerk browser runtime (~250 KiB of clerk-js +
 * ui-components + the first-party `vendor-clerk` chunk) must never load on a
 * signed-out marketing visit. The root gates the auth shell on this predicate,
 * evaluated identically on the server and the client from the internal,
 * prefix-rewritten pathname (the same one TanStack Router matches against), so
 * SSR and hydration always agree on the same tree.
 *
 * The decision is a DENY-LIST: public marketing / legal pages are enumerated in
 * {@link CLERK_FREE_PATHS} and render Clerk-free; EVERYTHING else defaults to
 * mounting Clerk. A new authenticated route is therefore safe by default (it
 * gets Clerk); only an explicitly listed public page opts out. To earn the
 * Clerk-free load, a future marketing page must be added here — and, because the
 * marketing components verifiably consume no Clerk state (no `useUser`/`useAuth`
 * hooks, no auth atoms), that opt-out is safe.
 *
 * Paths are the internal (prefix-rewritten) forms — e.g. the public
 * `clawinstall.ai/pricing` matches the route tree as `/clawinstall/pricing`.
 *
 * @since 1.0.0
 */
export const CLERK_FREE_PATHS: ReadonlySet<string> = new Set([
  "/clawinstall",
  "/clawinstall/acceptable-use",
  "/clawinstall/compare",
  "/clawinstall/faq",
  "/clawinstall/pricing",
  "/clawinstall/privacy",
  "/clawinstall/security",
  "/clawinstall/terms",
]);

/**
 * Does the given (internal) pathname need the Clerk auth shell mounted?
 *
 * `true` for every route except the enumerated public marketing / legal pages.
 * A trailing slash is normalized away so `/clawinstall/pricing/` and
 * `/clawinstall/pricing` resolve identically; the root `/` is preserved.
 *
 * @since 1.0.0
 */
export const routeNeedsClerk = (pathname: string): boolean => {
  const normalized = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  return !CLERK_FREE_PATHS.has(normalized);
};
